import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BookOpen, Camera, Users, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { HERO_IMAGE } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { AnimalCard } from "@/components/AnimalCard";
import { CampusMap } from "@/components/CampusMap";
import { RevealLine, ScrollReveal } from "@/components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    icon: Camera,
    title: "Observe",
    body: "Between classes and along the paths you walk every day, an entire wild community lives on our campus. Slow down. Look up into the elms, down into the pond. They have always been here.",
  },
  {
    n: "02",
    icon: BookOpen,
    title: "Record",
    body: "Every entry in this journal pairs a photograph with a piece of writing, a field note, a small story, a single observed moment. Students and teachers keep it together.",
  },
  {
    n: "03",
    icon: Users,
    title: "Share",
    body: "Like the encounters that move you, leave a note for the author, and find exactly where on campus each animal was spotted. The map remembers every sighting.",
  },
];

const MARQUEE_ITEMS = [
  "American Robin", "Baltimore Oriole", "Barn Swallow", "American Bullfrog",
  "American Black Duck", "Grey Squirrel", "Campus Wildlife", "Field Journal",
];

export default function Home() {
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => {
    api.get("/animals")
      .then(({ data }) => setAnimals(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      <Navbar />

      {/* Kinetic hero */}
      <section className="relative flex min-h-screen items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            srcSet="https://lh3.googleusercontent.com/d/13oNU0C2vdcO2d2D3AHYFWamuPBQjmuub=w900 900w, https://lh3.googleusercontent.com/d/13oNU0C2vdcO2d2D3AHYFWamuPBQjmuub=w1600 1600w, https://lh3.googleusercontent.com/d/13oNU0C2vdcO2d2D3AHYFWamuPBQjmuub=w2400 2400w"
            sizes="100vw"
            alt="Black ducks and mallards on the campus creek in winter"
            className="h-[125%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative w-full px-6 pb-20 pt-28 md:px-16 md:pb-24">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-6 text-xs font-bold uppercase tracking-[0.35em] text-[#f6f3ea]/80"
            data-testid="hero-eyebrow"
          >
            A living field journal of our school grounds
          </motion.p>
          <h1 className="font-serif text-[15vw] font-semibold leading-[0.88] tracking-tighter text-[#f6f3ea] md:text-[9.5vw]" data-testid="hero-title">
            <RevealLine delay={0.25}>The Wild</RevealLine>
            <RevealLine delay={0.4}>Side of</RevealLine>
            <RevealLine delay={0.55} className="italic">Campus.</RevealLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#gallery"
              data-testid="hero-browse-button"
              className="group flex items-center gap-3 bg-primary px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform duration-300 hover:-translate-y-1"
            >
              Browse the gallery
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/new"
              data-testid="hero-add-entry-button"
              className="border border-[#f6f3ea]/60 px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#f6f3ea] backdrop-blur-sm transition-colors duration-300 hover:bg-[#f6f3ea] hover:text-foreground"
            >
              Add an entry
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-10 text-[10px] font-bold uppercase leading-relaxed tracking-[0.18em] text-[#f6f3ea]/60 md:text-[11px] md:tracking-[0.3em]"
            data-testid="hero-credits"
          >
            Photos photographed by Ava Phipps · Web designed by Victoria Shao
          </motion.p>
        </motion.div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
          className="absolute bottom-8 right-8 hidden text-[#f6f3ea]/70 md:block"
        >
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </section>

      {/* Editorial ribbon */}
      <div className="border-y border-border bg-foreground py-5 text-background" data-testid="editorial-marquee">
        <Marquee speed={45} gradient={false}>
          {MARQUEE_ITEMS.map((item) => (
            <span key={item} className="mx-8 flex items-center gap-8 font-serif text-2xl italic md:text-3xl">
              {item}
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* Manifesto chapters */}
      <section className="mx-auto max-w-7xl px-6 py-28 md:px-16 md:py-40">
        <ScrollReveal>
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">Why this journal exists</p>
          <h2 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-[1.02] tracking-tight md:text-6xl" data-testid="manifesto-heading">
            The campus is not just ours. We are keeping its other residents on the record.
          </h2>
        </ScrollReveal>
        <div className="mt-20 grid gap-16 md:grid-cols-3 md:gap-10">
          {CHAPTERS.map((c, i) => (
            <ScrollReveal key={c.n} delay={i * 0.12}>
              <div className="border-t-2 border-foreground pt-8" data-testid={`manifesto-chapter-${c.n}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-6xl font-semibold text-primary/25">{c.n}</span>
                  <c.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="mt-6 font-serif text-3xl font-semibold tracking-tight">{c.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{c.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="mx-auto max-w-7xl scroll-mt-24 px-6 pb-28 md:px-16 md:pb-40">
        <ScrollReveal className="mb-14 flex flex-wrap items-end justify-between gap-6 border-t border-border pt-14">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">Field notes</p>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight md:text-6xl" data-testid="gallery-heading">
              The Residents
            </h2>
          </div>
          <p className="text-sm text-muted-foreground" data-testid="gallery-count">
            {loading ? "Opening the journal…" : `${animals.length} ${animals.length === 1 ? "entry" : "entries"} so far`}
          </p>
        </ScrollReveal>

        {loading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12" data-testid="gallery-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`animate-pulse border border-border bg-secondary ${i === 0 ? "h-[480px] md:col-span-8 md:row-span-2" : "h-64 md:col-span-4"}`} />
            ))}
          </div>
        ) : animals.length === 0 ? (
          <div className="border border-dashed border-border p-20 text-center" data-testid="gallery-empty">
            <p className="font-serif text-2xl italic text-muted-foreground">No entries yet. Be the first to record a sighting.</p>
            <Link to="/new" data-testid="gallery-empty-cta" className="mt-6 inline-block bg-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground">
              Add the first entry
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12" data-testid="gallery-grid">
            {animals.map((a, i) => (
              <AnimalCard key={a.post_id} animal={a} featured={i === 0} />
            ))}
          </div>
        )}
      </section>

      {/* Map */}
      <section id="map" className="mx-auto max-w-7xl scroll-mt-24 px-6 pb-28 md:px-16 md:pb-40">
        <ScrollReveal className="mb-14 border-t border-border pt-14">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">Sightings</p>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight md:text-6xl" data-testid="map-heading">
            Where they roam
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
            Every entry is pinned to the exact spot on campus where the animal was seen. Walk the map, then walk the campus.
          </p>
        </ScrollReveal>
        <ScrollReveal>
          <CampusMap animals={animals} />
        </ScrollReveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-foreground text-background" data-testid="footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-16 md:flex-row md:items-end md:justify-between md:px-16">
          <div>
            <p className="font-serif text-4xl font-semibold italic tracking-tight md:text-5xl">Keep looking up.</p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-background/60">
              A student-run field journal of the wild residents of our school campus. Written, photographed and mapped by the people who walk these paths every day.
            </p>
          </div>
          <Link
            to="/new"
            data-testid="footer-add-entry-button"
            className="group flex w-fit items-center gap-3 border border-background/40 px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-background hover:text-foreground"
          >
            Contribute a sighting
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="border-t border-background/15 py-6 text-center text-xs uppercase tracking-[0.3em] text-background/40">
          Campus Field Journal — 2026
        </div>
      </footer>
    </div>
  );
}
